import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Package } from 'lucide-react';
import { Button, Checkbox } from '@grofers/dock-design-kit';
import { AddItemButtonCustom } from '../../../../UiKit/addItemButtonCustom/addItemButtonCustom';

// Tags that mean this item was already actioned on a previous attempt -
// it can't be picked again even though max_quantity is 0 like any other
// maxed-out item, so it needs its own "why" badge rather than just
// looking selectable-but-stuck-at-zero.
const TERMINAL_TAG_SLUGS = [
  'ALREADY_REFUNDED',
  'ALREADY_REPLACED',
  'ALREADY_RESOLVED',
  'ALREADY_ACKNOWLEDGED',
  'EXCHANGED',
];

const getTerminalTag = (item) =>
  item.max_quantity === 0
    ? (item.tags || []).find((tag) => TERMINAL_TAG_SLUGS.includes(tag.slug))
    : null;

/**
 * "Add more items from order" control - a custom picker (dock's Dropdown
 * only supports plain label rows) that lists the remaining order items
 * with their image, name/qty and a stepper each, plus a "Select all"
 * shortcut and Cancel/Add actions, matching the Figma/product spec.
 *
 * The panel is rendered in a portal and positioned via the trigger's
 * bounding rect (recomputed on scroll/resize) instead of being absolutely
 * positioned inside the accordion - the accordion card clips overflow to
 * keep its rounded corners, which was cutting the panel (and its inner
 * scroll area) off.
 */
const ItemPickerDropdown = ({
  items = [],
  selectedCounts = {},
  onApply = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const [draftCounts, setDraftCounts] = useState(selectedCounts);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Terminally-tagged items can't be picked, so they're pushed to the
  // bottom of the list rather than cluttering the top - a stable sort
  // keeps everything else in its original order.
  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => Boolean(getTerminalTag(a)) - Boolean(getTerminalTag(b)),
      ),
    [items],
  );

  useEffect(() => {
    if (open) {
      setDraftCounts(selectedCounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.bottom + 4;
      setPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(160, Math.min(400, window.innerHeight - top - 16)),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Count of items with a nonzero quantity in the in-progress draft - used
  // for panel-internal state (select-all, Add button).
  const draftSelectedCount = Object.values(draftCounts).filter(
    (c) => c > 0,
  ).length;
  const allSelected = items.length > 0 && draftSelectedCount === items.length;
  const someSelected = draftSelectedCount > 0 && !allSelected;

  // The trigger label reflects the committed selection (the `selectedCounts`
  // prop from the parent), not the draft - once "Add" promotes items out of
  // the pool, draftCounts still holds their old nonzero values even though
  // they're no longer part of `items`, which kept the label stuck showing
  // a stale count after the panel closed.
  const committedSelectedCount = Object.values(selectedCounts).filter(
    (c) => c > 0,
  ).length;

  const updateCount = (item, delta) => {
    console.log(`updateCount: ${item.text} delta=${delta}`);

    setDraftCounts((prev) => {
      const max = item.max_quantity ?? Infinity;
      const next = Math.min(max, Math.max(0, (prev[item.id] || 0) + delta));
      return { ...prev, [item.id]: next };
    });
  };

  const toggleSelectAll = () => {
    console.log('clicked toggleSelectAll');
    const shouldSelectAll = !allSelected;
    const next = {};
    items.forEach((item) => {
      next[item.id] = shouldSelectAll ? Math.min(1, item.max_quantity ?? 1) : 0;
    });
    setDraftCounts(next);
  };

  const handleAdd = () => {
    onApply(draftCounts);
    setOpen(false);
  };

  const handleCancel = () => {
    setDraftCounts(selectedCounts);
    setOpen(false);
  };

  return (
    <div className='tw-w-full'>
      <button
        ref={triggerRef}
        type='button'
        disabled={items.length === 0}
        onClick={() => setOpen((prev) => !prev)}
        className='tw-flex tw-items-center tw-justify-between tw-w-full tw-bg-white tw-border tw-border-solid tw-border-[#d2d6db] tw-rounded-[8px] tw-px-[10px] tw-py-[8px] tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-bg-[#f9fafb]'
      >
        <span className='tw-text-[14px] tw-leading-[20px] tw-text-[#6c737f]'>
          {items.length === 0
            ? 'All items selected'
            : committedSelectedCount > 0
            ? `${committedSelectedCount} item(s) selected`
            : 'Select items'}
        </span>
        <ChevronDown
          size={18}
          className={`tw-text-[#6c737f] tw-shrink-0 tw-transition-transform tw-duration-200 ${
            open ? 'tw-rotate-180' : ''
          }`}
        />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 9999,
            }}
            className='tw-bg-white tw-border tw-border-solid tw-border-[#e5e7eb] tw-rounded-[12px] tw-shadow-[0_4px_16px_rgba(0,0,0,0.12)]'
          >
            <div
              style={{ flexShrink: 0 }}
              className='tw-flex tw-items-center tw-gap-2 tw-px-[12px] tw-py-[10px] tw-border-0 tw-border-b tw-border-solid tw-border-[#e5e7eb] tw-cursor-pointer'
              onClick={toggleSelectAll}
              role='button'
              tabIndex={0}
            >
              <Checkbox
                isSelected={allSelected}
                intermediate={someSelected}
                onChange={toggleSelectAll}
                size='small'
              />
              <span className='tw-text-[14px] tw-leading-[18px] tw-text-[#111927]'>
                Select all
              </span>
            </div>

            <div
              style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
              className='tw-flex tw-flex-col'
            >
              {sortedItems.map((item) => {
                const count = draftCounts[item.id] || 0;
                const terminalTag = getTerminalTag(item);
                return (
                  <div
                    key={item.id}
                    className='tw-flex tw-items-center tw-gap-2 tw-px-[12px] tw-py-[10px] tw-border-0 tw-border-b tw-border-solid tw-border-[#f3f4f6] last:tw-border-b-0'
                  >
                    <div className='tw-size-[48px] tw-rounded-[8px] tw-bg-[#f3f4f6] tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-overflow-hidden'>
                      {item.item_image_url ? (
                        <img
                          src={item.item_image_url}
                          alt={item.text}
                          className='tw-size-full tw-object-cover'
                        />
                      ) : (
                        <Package size={20} className='tw-text-[#9da4ae]' />
                      )}
                    </div>
                    <div className='tw-flex tw-flex-col tw-gap-[2px] tw-min-w-0 tw-flex-1'>
                      {terminalTag && (
                        <span
                          style={{
                            backgroundColor: terminalTag.bg_color,
                            color: terminalTag.color,
                          }}
                          className='tw-self-start tw-text-[11px] tw-font-medium tw-leading-[14px] tw-rounded-[4px] tw-px-[6px] tw-py-[2px] tw-mb-[2px]'
                        >
                          {terminalTag.label}
                        </span>
                      )}
                      <p className='tw-text-[13px] tw-leading-[16px] tw-font-medium tw-text-[#111927] tw-m-0 tw-line-clamp-2'>
                        {item.text}
                      </p>
                      {item.description && (
                        <p className='tw-text-[11px] tw-leading-[14px] tw-text-[#6c737f] tw-m-0'>
                          {item.description}
                        </p>
                      )}
                    </div>
                    <div className='tw-shrink-0'>
                      <AddItemButtonCustom
                        count={count}
                        handleIncrease={() => updateCount(item, 1)}
                        handleDecrease={() => updateCount(item, -1)}
                        increaseDisabled={
                          Boolean(terminalTag) ||
                          count >= (item.max_quantity ?? Infinity)
                        }
                        decreaseDisabled={Boolean(terminalTag)}
                        disabled={Boolean(terminalTag)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{ flexShrink: 0 }}
              className='tw-flex tw-items-center tw-justify-end tw-gap-[8px] tw-px-[12px] tw-py-[10px] tw-border-0 tw-border-t tw-border-solid tw-border-[#e5e7eb]'
            >
              <Button
                variant='secondary'
                buttonType='default'
                size='sm'
                shape='square'
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                buttonType='default'
                size='sm'
                shape='square'
                disabled={draftSelectedCount === 0}
                onClick={handleAdd}
              >
                Add
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ItemPickerDropdown;
